import { Component, OnInit, HostListener, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { environment } from 'src/environments/environment';

interface ApiKeyItem {
  _id: string;
  name: string;
  keyPrefix: string;
  allowedProjects?: string[];
  createdAt: string;
  lastUsedAt?: string;
  decryptedKey?: string;
}

interface VcaasCreditCosts {
  CREATE_PROJECT: number;
  CREATE_DEPLOYMENT: number;
  START_SERVER: number;
  GET_SOURCE_CODE: number;
  RECOVER_VERSION: number;
  UPLOAD_FILE: number;
  ADD_CUSTOM_DOMAIN: number;
  EXPORT_PROJECT: number;
  IMPORT_PROJECT: number;
}

type WebhookEventType = 'agent.prompt.finished' | 'project.credit_limit.reached';

/**
 * One row of the project-scope picker. `apiAccessEnabled` is the SAME rule the
 * backend enforces (VCaaSController.isApiAccessAllowed): on the `api` plan access
 * is on unless explicitly disabled, on every other plan it must be explicitly
 * enabled. Projects that fail it are still LISTED (greyed, unselectable) so the
 * owner learns why a project is missing instead of hunting for it.
 */
interface ProjectOption {
  organizationId: string;
  label?: string;
  apiAccessEnabled: boolean;
}

/** Rows fetched per search. The server caps at 100; 50 keeps the dropdown fast. */
const PROJECT_PICKER_PAGE_SIZE = 50;
const PROJECT_PICKER_DEBOUNCE_MS = 250;

interface WebhookItem {
  id: string;
  url: string;
  headers?: Record<string, string>;
  event: WebhookEventType;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-vcaas',
  templateUrl: './vcaas.page.html',
  styleUrls: ['./vcaas.page.scss']
})
export class VcaasPage implements OnInit {

  apiKeys: ApiKeyItem[] = [];
  userData: any = null;
  isLoading = true;
  showDocs = false;
  showManageKeys = false;
  copiedKey = false;
  copiedKeyId: string | null = null;
  readonly detailedDocsUrl = 'https://www.totalum.app/docs';
  newKeyName = '';
  creatingKey = false;
  nameError = '';
  copiedDocs = false;
  // Cached copy of the canonical API reference markdown fetched from the
  // totalum-website (`environment.docsMarkdownUrl`). Warmed on page entry so the
  // copy/download actions resolve instantly and stay within the click gesture.
  private cachedDocsMarkdown: string | null = null;
  /** Shown when the reference could not be fetched — see `fetchFullDocs`. */
  docsLoadFailed = false;
  private docsFetchPromise: Promise<string> | null = null;
  visibleKeyId: string | null = null;
  expandedEndpoint = '';
  activeTab: Record<string, string> = {};
  companyDomain = '';
  creditCosts: VcaasCreditCosts | null = null;
  showMcpDocs = false;
  mcpActiveAgent = 'openclaw';
  mcpShowFullKey = false;
  mcpCopied = false;

  showManageWebhooks = false;
  webhooks: WebhookItem[] = [];
  newWebhookUrl = '';
  newWebhookEvent: WebhookEventType | '' = '';
  newWebhookHeaders: { key: string; value: string }[] = [];
  webhookError = '';
  creatingWebhook = false;
  allowedWebhookEvents: WebhookEventType[] = ['agent.prompt.finished', 'project.credit_limit.reached'];

  // Project-scoped API keys
  newKeyRestrictProjects = false;
  newKeyProjects: string[] = [];
  newKeyProjectSearch = '';
  newKeyProjectDropdownOpen = false;
  editingKeyId: string | null = null;
  editKeyProjects: string[] = [];
  editKeyProjectSearch = '';
  editKeyDropdownOpen = false;
  savingKeyProjects = false;

  /**
   * Project picker rows, shared by the create-key and edit-key dropdowns (only one
   * is ever open at a time). Fetched from the paged /instance/list endpoint, NOT
   * from `userData.totalumInstances`: that array is capped at 100 oldest projects
   * and only carries organizationIds, so newer projects could never be found and
   * searching by the project's visible name matched nothing.
   */
  projectOptions: ProjectOption[] = [];
  projectOptionsTotal = 0;
  projectOptionsLoading = false;
  private projectSearchTimer: any = null;
  private projectSearchSeq = 0;

  // Notification preferences
  emailOnFirstPromptCompleted = true;
  emailOnLimitReached = true;
  savingNotifications = false;

  constructor(private apiSdkService: ApiSdkService, private router: Router, private elementRef: ElementRef) {}

  /**
   * Locks API key visibility for users that have never purchased credits AND
   * have a credit balance below 1. Re-evaluated on every page entry because
   * ngOnInit calls getFullUserData(), which returns fresh credit/purchase data.
   */
  get apiKeyLocked(): boolean {
    if (!this.userData) return true;
    const purchasedEuros = Number(this.userData.totalCreditsPurchasedEuros ?? 0);
    const balance = Number(this.userData.credits ?? 0);
    return purchasedEuros <= 0 && balance < 1;
  }

  goBuyCredits(): void {
    this.router.navigate(['/account'], { queryParams: { openCredits: 1 } });
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.newKeyProjectDropdownOpen && !target.closest('.project-scope-dropdown')) {
      this.newKeyProjectDropdownOpen = false;
    }
    if (this.editKeyDropdownOpen && !target.closest('.edit-project-dropdown')) {
      this.editKeyDropdownOpen = false;
    }
  }

  async ngOnInit() {
    this.prefetchDocs();
    await this.refreshAll();
  }

  // Re-runs every time the user navigates back to /vcaas (e.g. after buying
  // credits on /account), guaranteeing the lock state is re-evaluated against
  // fresh credit balance and purchase totals.
  async ionViewWillEnter() {
    if (this.userData) {
      await this.refreshUserDataOnly();
    }
  }

  private async refreshAll() {
    const result = await this.apiSdkService.getFullUserData();
    const user = result?.success?.data;
    this.userData = user;
    this.companyDomain = this.extractCompanyDomain(user.email);

    // Load notification preferences from user data (default to true)
    this.emailOnFirstPromptCompleted = user.vcaasNotifications?.emailOnFirstPromptCompleted ?? true;
    this.emailOnLimitReached = user.vcaasNotifications?.emailOnLimitReached ?? true;

    await Promise.all([this.loadApiKeys(), this.loadCreditCosts(), this.loadWebhooks()]);
  }

  private async refreshUserDataOnly() {
    const result = await this.apiSdkService.getFullUserData();
    if (result?.success?.data) {
      this.userData = result.success.data;
    }
  }

  async loadCreditCosts() {
    try {
      const result = await this.apiSdkService.getVcaasCreditCosts();
      this.creditCosts = result?.success?.data?.creditCosts || null;
    } catch (error) {
      console.error('Error loading credit costs:', error);
    }
  }

  async loadWebhooks() {
    try {
      const result = await this.apiSdkService.getWebhooks();
      this.webhooks = result?.success?.data?.webhooks || [];
    } catch (error) {
      console.error('Error loading webhooks:', error);
    }
  }

  async createWebhook() {
    const url = this.newWebhookUrl.trim();
    const event = this.newWebhookEvent;

    if (!url || !event) {
      this.webhookError = 'URL and event are required';
      return;
    }

    if (!url.startsWith('https://')) {
      this.webhookError = 'URL must use HTTPS';
      return;
    }

    this.webhookError = '';
    this.creatingWebhook = true;
    try {
      const headers: Record<string, string> = {};
      for (const h of this.newWebhookHeaders) {
        if (h.key.trim() && h.value.trim()) {
          headers[h.key.trim()] = h.value.trim();
        }
      }
      const result = await this.apiSdkService.createWebhook(url, event, Object.keys(headers).length > 0 ? headers : undefined);
      if (result?.success?.data) {
        await this.loadWebhooks();
        this.newWebhookUrl = '';
        this.newWebhookEvent = '';
        this.newWebhookHeaders = [];
      } else if (result?.error) {
        this.webhookError = result.error.data?.errors?.errorMessage || 'Error creating webhook';
      }
    } catch (error: any) {
      this.webhookError = error?.error?.errors?.errorMessage || 'Error creating webhook';
    } finally {
      this.creatingWebhook = false;
    }
  }

  async deleteWebhook(webhookId: string) {
    if (!confirm('Are you sure you want to delete this webhook?')) return;
    try {
      await this.apiSdkService.deleteWebhook(webhookId);
      this.webhooks = this.webhooks.filter(w => w.id !== webhookId);
    } catch (error) {
      console.error('Error deleting webhook:', error);
    }
  }

  addWebhookHeader() {
    this.newWebhookHeaders.push({ key: '', value: '' });
  }

  removeWebhookHeader(index: number) {
    this.newWebhookHeaders.splice(index, 1);
  }

  async saveNotificationPreferences() {
    this.savingNotifications = true;
    try {
      await this.apiSdkService.updateVcaasNotifications({
        emailOnFirstPromptCompleted: this.emailOnFirstPromptCompleted,
        emailOnLimitReached: this.emailOnLimitReached,
      });
    } catch (error) {
      console.error('Error saving notification preferences:', error);
    } finally {
      this.savingNotifications = false;
    }
  }

  getCreditCost(key: keyof VcaasCreditCosts): string {
    if (!this.creditCosts) return '...';
    return `${this.creditCosts[key]} credits`;
  }

  async loadApiKeys() {
    this.isLoading = true;
    try {
      const result = await this.apiSdkService.getApiKeys();
      this.apiKeys = result?.success?.data || [];

      // Auto-create a default API key if none exists
      if (this.apiKeys.length === 0) {
        await this.createApiKey('Default');
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async createApiKey(name?: string) {
    const keyName = name || this.newKeyName.trim();
    if (!keyName) return;

    // Check for duplicate names
    if (this.apiKeys.some(k => k.name === keyName)) {
      this.nameError = 'An API key with this name already exists';
      return;
    }

    this.nameError = '';
    this.creatingKey = true;
    try {
      const allowedProjects = this.newKeyRestrictProjects && this.newKeyProjects.length > 0 ? this.newKeyProjects : undefined;
      const result = await this.apiSdkService.createApiKey(keyName, allowedProjects);
      if (result?.success?.data) {
        await this.loadApiKeys();
        this.newKeyName = '';
        this.newKeyRestrictProjects = false;
        this.newKeyProjects = [];
        this.newKeyProjectSearch = '';
      }
    } catch (error) {
      console.error('Error creating API key:', error);
    } finally {
      this.creatingKey = false;
    }
  }

  /** Called on focus and on every keystroke of either picker input. */
  onProjectSearchChange(search: string): void {
    if (this.projectSearchTimer) clearTimeout(this.projectSearchTimer);
    this.projectSearchTimer = setTimeout(() => this.searchProjects(search), PROJECT_PICKER_DEBOUNCE_MS);
  }

  openProjectPicker(which: 'new' | 'edit'): void {
    if (which === 'new') this.newKeyProjectDropdownOpen = true;
    else this.editKeyDropdownOpen = true;
    const search = which === 'new' ? this.newKeyProjectSearch : this.editKeyProjectSearch;
    this.searchProjects(search);
  }

  private async searchProjects(search: string): Promise<void> {
    const seq = ++this.projectSearchSeq;
    this.projectOptionsLoading = true;
    try {
      const result = await this.apiSdkService.listInstances({
        search: (search || '').trim() || undefined,
        limit: PROJECT_PICKER_PAGE_SIZE,
        sortField: 'name',
        sortDirection: 'asc',
      });
      // A slower, older request must never overwrite the answer to a newer keystroke.
      if (seq !== this.projectSearchSeq) return;
      const data = result?.success?.data;
      const instances: any[] = data?.instances || [];
      this.projectOptions = instances.map((i: any) => ({
        organizationId: i.organizationId,
        label: i.label || undefined,
        apiAccessEnabled: i.plan === 'api' ? i.apiAccessEnabled !== false : i.apiAccessEnabled === true,
      }));
      this.projectOptionsTotal = Number(data?.total ?? this.projectOptions.length);
    } catch (e) {
      if (seq !== this.projectSearchSeq) return;
      console.error('Error searching projects:', e);
      this.projectOptions = [];
      this.projectOptionsTotal = 0;
    } finally {
      if (seq === this.projectSearchSeq) this.projectOptionsLoading = false;
    }
  }

  trackProjectOption(_: number, p: ProjectOption): string {
    return p.organizationId;
  }

  toggleKeyProject(projectId: string, option?: ProjectOption): void {
    if (option && !option.apiAccessEnabled) return;
    const idx = this.newKeyProjects.indexOf(projectId);
    if (idx >= 0) {
      this.newKeyProjects.splice(idx, 1);
    } else if (this.newKeyProjects.length < 300) {
      this.newKeyProjects.push(projectId);
    }
  }

  removeKeyProject(projectId: string): void {
    this.newKeyProjects = this.newKeyProjects.filter(p => p !== projectId);
  }

  startEditKeyProjects(key: ApiKeyItem): void {
    this.editingKeyId = key._id;
    this.editKeyProjects = [...(key.allowedProjects || [])];
    this.editKeyProjectSearch = '';
    this.editKeyDropdownOpen = false;
  }

  cancelEditKeyProjects(): void {
    this.editingKeyId = null;
    this.editKeyProjects = [];
    this.editKeyProjectSearch = '';
  }

  toggleEditKeyProject(projectId: string, option?: ProjectOption): void {
    if (option && !option.apiAccessEnabled) return;
    const idx = this.editKeyProjects.indexOf(projectId);
    if (idx >= 0) {
      this.editKeyProjects.splice(idx, 1);
    } else if (this.editKeyProjects.length < 300) {
      this.editKeyProjects.push(projectId);
    }
  }

  removeEditKeyProject(projectId: string): void {
    this.editKeyProjects = this.editKeyProjects.filter(p => p !== projectId);
  }

  async saveEditKeyProjects(): Promise<void> {
    if (!this.editingKeyId) return;
    this.savingKeyProjects = true;
    try {
      const projects = this.editKeyProjects.length > 0 ? this.editKeyProjects : null;
      await this.apiSdkService.updateApiKeyAllowedProjects(this.editingKeyId, projects);
      // Update local state
      const key = this.apiKeys.find(k => k._id === this.editingKeyId);
      if (key) {
        key.allowedProjects = projects || undefined;
      }
      this.editingKeyId = null;
      this.editKeyProjects = [];
    } catch (e) {
      console.error('Error updating allowed projects:', e);
    } finally {
      this.savingKeyProjects = false;
    }
  }

  async deleteApiKey(keyId: string) {
    if (!confirm('Are you sure you want to delete this API key?')) return;
    try {
      await this.apiSdkService.deleteApiKey(keyId);
      this.apiKeys = this.apiKeys.filter(k => k._id !== keyId);
      if (this.visibleKeyId === keyId) this.visibleKeyId = null;
    } catch (error) {
      console.error('Error deleting API key:', error);
    }
  }

  async revealApiKey(key: ApiKeyItem) {
    if (key.decryptedKey) {
      key.decryptedKey = undefined;
      return;
    }
    try {
      const result = await this.apiSdkService.getDecryptedApiKey(key._id);
      if (result?.success?.data?.apiKey) {
        key.decryptedKey = result.success.data.apiKey;
      }
    } catch (error) {
      console.error('Error decrypting API key:', error);
    }
  }

  get displayKey(): string {
    const key = this.apiKeys[0];
    return key ? key.keyPrefix : 'No API key available';
  }

  private extractCompanyDomain(email: string): string {
    if (!email) return '';
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return '';
    const genericDomains = [
      'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.es', 'outlook.com', 'outlook.es',
      'yahoo.com', 'yahoo.es', 'proton.me', 'protonmail.com', 'live.com', 'msn.com',
      'icloud.com', 'me.com', 'mac.com', 'aol.com', 'mail.com', 'zoho.com',
      'yandex.com', 'gmx.com', 'gmx.net', 'tutanota.com', 'fastmail.com'
    ];
    if (genericDomains.includes(domain)) return '';
    return domain;
  }

  async copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.copiedKey = true;
      setTimeout(() => this.copiedKey = false, 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
  }


  /**
   * Copies the full API key to the clipboard WITHOUT ever rendering it in the
   * DOM. The decrypted value is fetched on demand and used only for the copy.
   */
  async copyApiKey(key: ApiKeyItem) {
    try {
      let full = key.decryptedKey;
      if (!full) {
        const result = await this.apiSdkService.getDecryptedApiKey(key._id);
        full = result?.success?.data?.apiKey;
      }
      if (!full) return;
      await navigator.clipboard.writeText(full);
      this.copiedKeyId = key._id;
      this.copiedKey = key._id === this.apiKeys[0]?._id;
      setTimeout(() => {
        if (this.copiedKeyId === key._id) this.copiedKeyId = null;
        this.copiedKey = false;
      }, 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
  }

  /** Masked representation of a key — prefix followed by dots, never the full value. */
  maskedKey(key: ApiKeyItem): string {
    if (!key?.keyPrefix) return '••••••••••••';
    return `${key.keyPrefix}••••••••••••••••`;
  }

  /**
   * ═══⭐⭐ THE API REFERENCE IS FETCHED, NEVER VENDORED ═════════════════════
   *
   * `environment.docsMarkdownUrl` → `https://www.totalum.app/totalum-api.md`,
   * which totalum-website generates from the same content that renders
   * `www.totalum.app/docs/api/*`. It is public and CORS-open for exactly this
   * reuse, so this page, the new platform's `/api` page and the website's own
   * download button all hand out the SAME bytes.
   *
   * ⚠️⚠️ THERE USED TO BE A 2,050-LINE COPY OF THE DOCS IN THIS FILE, and it was
   * the fallback for this method. Deleted, deliberately and permanently:
   *
   *   · **It was silently, badly stale.** It knew nothing about the Figma
   *     endpoints, the read/write file API, project groups, project transfer or
   *     `POST /projects/launch` — it did not contain the word "figma" or "launch"
   *     even once. An agent handed that file is told about an API that has not
   *     existed for months, and NOTHING in the download says so.
   *   · **A wrong reference is worse than a failed download.** A failure is
   *     visible and the user retries; a stale reference produces confident code
   *     against endpoints that 404.
   *   · **It shipped to every visitor** as ~60 kB of JavaScript in the page
   *     bundle, whether or not they ever pressed the button.
   *
   * ⚠️ SO A FAILURE NOW THROWS AND IS SHOWN. Do not "restore the fallback" — the
   * only correct offline behaviour is to say the docs could not be loaded and
   * point at the URL, which is what the callers below do.
   *
   * The result is cached; concurrent callers share a single in-flight request.
   */
  private async fetchFullDocs(): Promise<string> {
    if (this.cachedDocsMarkdown) return this.cachedDocsMarkdown;
    if (this.docsFetchPromise) return this.docsFetchPromise;
    this.docsFetchPromise = (async () => {
      try {
        // Plain GET, no custom headers → CORS "simple request"; the website
        // route sends Access-Control-Allow-Origin so the read succeeds.
        const res = await fetch(environment.docsMarkdownUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = (await res.text()).trim();
        if (!text) throw new Error('empty docs response');
        this.cachedDocsMarkdown = text;
        return text;
      } finally {
        this.docsFetchPromise = null;
      }
    })();
    return this.docsFetchPromise;
  }

  /** Warms the docs cache in the background so the button clicks stay instant. */
  private prefetchDocs(): void {
    // Best-effort warm-up: a failure here is retried, and reported, on click.
    if (!this.cachedDocsMarkdown) this.fetchFullDocs().catch(() => undefined);
  }

  async downloadDocsAsMarkdown() {
    let docs: string;
    try {
      docs = await this.fetchFullDocs();
    } catch (e) {
      console.error('[vcaas] Could not load the API reference', e);
      this.docsLoadFailed = true;
      setTimeout(() => this.docsLoadFailed = false, 6000);
      return;
    }
    const blob = new Blob([docs], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'totalum-vcaas-api-docs.md';
    a.click();
    URL.revokeObjectURL(url);
  }

  async copyDocsForLLMs() {
    let docs: string;
    try {
      docs = await this.fetchFullDocs();
    } catch (e) {
      console.error('[vcaas] Could not load the API reference', e);
      this.docsLoadFailed = true;
      setTimeout(() => this.docsLoadFailed = false, 6000);
      return;
    }
    try {
      await navigator.clipboard.writeText(docs);
      this.copiedDocs = true;
      setTimeout(() => this.copiedDocs = false, 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
  }

  get mcpApiKeyDisplay(): string {
    if (this.mcpShowFullKey && this.apiKeys[0]?.decryptedKey) {
      return this.apiKeys[0].decryptedKey;
    }
    return 'tlm_sk_****';
  }

  async copyMcpSnippet() {
    const el = document.querySelector('.mcp-snippet-active code');
    if (el) {
      try {
        await navigator.clipboard.writeText(el.textContent || '');
        this.mcpCopied = true;
        setTimeout(() => this.mcpCopied = false, 2000);
      } catch (e) { console.error('Copy failed', e); }
    }
  }

  async toggleMcpShowKey() {
    this.mcpShowFullKey = !this.mcpShowFullKey;
    if (this.mcpShowFullKey && this.apiKeys[0] && !this.apiKeys[0].decryptedKey) {
      await this.revealApiKey(this.apiKeys[0]);
    }
  }

  formatDate(date: string | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

}
